import { useEffect, useState } from 'react';
import type { QuoteResponse, ShipperQuoteStatus } from '@/types/quote';
import { ExtractedDataPanel } from './extracted-data-panel';
import { useTranslation } from 'react-i18next';
import { Mail01, X, CheckCircle, AlertCircle, Save01 } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Badge } from '@/components/base/badges/badges';
import { Input } from '@/components/base/input/input';
import { TextArea } from '@/components/base/textarea/textarea';

interface QuoteResponseDetailPanelProps {
    quote: QuoteResponse;
    onClose: () => void;
    onMarkAsRead?: (quoteId: string) => void;
    onAccept?: (quoteId: string) => void;
    onReject?: (quoteId: string) => void;
    onRequestModification?: (quoteId: string, body?: string, subject?: string) => void;
    quoteRequestStatus?: ShipperQuoteStatus;
    initialModificationEditorOpen?: boolean;
}

export const QuoteResponseDetailPanel = ({ quote, onClose, onMarkAsRead, onAccept, onReject, onRequestModification, quoteRequestStatus, initialModificationEditorOpen = false }: QuoteResponseDetailPanelProps) => {
    const { t } = useTranslation(['pages:quotes', 'common']);
    const [activeTab, setActiveTab] = useState<'thread' | 'extracted'>('thread');
    const [modificationSubject, setModificationSubject] = useState(
        t('pages:quotes:shipper.responses.modificationSubjectDefault', { defaultValue: `Richiesta modifica preventivo ${quote.subject}` })
    );
    const [modificationBody, setModificationBody] = useState('');
    const [isModificationEditorOpen, setIsModificationEditorOpen] = useState(initialModificationEditorOpen);

    // Marca come letta quando viene visualizzata
    useEffect(() => {
        if (onMarkAsRead && !quote.isRead) {
            onMarkAsRead(quote.id);
        }
    }, [quote.id, quote.isRead, onMarkAsRead]);

    const isActionDisabled = quoteRequestStatus === 'completed' || quoteRequestStatus === 'cancelled';

    const getStatusBadge = () => {
        if (!quote.status || quote.status === 'pending') return null;

        switch (quote.status) {
            case 'accepted':
                return (
                    <Badge color="success" size="sm">
                        {t('shipper.responses.accepted') || 'Accettata'}
                    </Badge>
                );
            case 'rejected':
                return (
                    <Badge color="error" size="sm">
                        {t('shipper.responses.rejected') || 'Rifiutata'}
                    </Badge>
                );
            case 'modification_requested':
                return (
                    <Badge color="warning" size="sm">
                        {t('shipper.responses.modificationRequested') || 'Modifica Richiesta'}
                    </Badge>
                );
            default:
                return null;
        }
    };

    return (
        <div className="flex flex-col h-full bg-primary border-l border-secondary">
            {/* Header */}
            <div className="flex-shrink-0 px-6 py-4 border-b border-secondary">
                <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                        <Mail01 className="h-5 w-5 text-tertiary flex-shrink-0" />
                        <h3 className="text-lg font-semibold text-primary truncate">{quote.subject}</h3>
                        {getStatusBadge()}
                    </div>
                    <Button
                        onClick={onClose}
                        color="secondary"
                        size="sm"
                        iconLeading={X}
                        aria-label={t('common:close') || 'Close'}
                    />
                </div>
            </div>

            {/* Summary Info */}
            <div className="flex-shrink-0 px-6 py-4 border-b border-secondary">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <div className="text-xs font-medium text-tertiary mb-1">
                            {t('shipper.responses.from') || 'Da'}
                        </div>
                        <div className="text-sm text-primary font-semibold">
                            {quote.fromName || quote.from}
                        </div>
                    </div>
                    <div>
                        <div className="text-xs font-medium text-tertiary mb-1">
                            {t('shipper.responses.price') || 'Prezzo'}
                        </div>
                        <div className="text-lg font-semibold text-primary">
                            {quote.price.toLocaleString('it-IT')} {quote.currency}
                        </div>
                    </div>
                    {quote.validity && (
                        <div>
                            <div className="text-xs font-medium text-tertiary mb-1">
                                {t('shipper.responses.validity') || 'Validità'}
                            </div>
                            <div className="text-sm text-primary">
                                {quote.validity} {t('shipper.responses.days') || 'giorni'}
                            </div>
                        </div>
                    )}
                    {quote.terms && (
                        <div>
                            <div className="text-xs font-medium text-tertiary mb-1">
                                {t('shipper.responses.terms') || 'Termini e Condizioni'}
                            </div>
                            <div className="text-sm text-primary line-clamp-2">
                                {quote.terms}
                            </div>
                        </div>
                    )}
                    {quote.status === 'accepted' && quote.bookingNumber && (
                        <div className="col-span-2">
                            <div className="text-xs font-medium text-tertiary mb-1">
                                {t('shipper.responses.bookingNumber') || 'Booking Number'}
                            </div>
                            <div className="text-sm font-semibold text-success-600">
                                {quote.bookingNumber}
                            </div>
                        </div>
                    )}
                    {quote.status === 'accepted' && quote.bookingNumberRequested && !quote.bookingNumber && (
                        <div className="col-span-2">
                            <div className="text-xs font-medium text-tertiary mb-1">
                                {t('shipper.responses.bookingNumber') || 'Booking Number'}
                            </div>
                            <div className="text-sm text-warning-600 flex items-center gap-2">
                                <AlertCircle className="h-4 w-4" />
                                {t('shipper.responses.bookingNumberRequested') || 'Richiesta in corso...'}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className="flex-shrink-0 flex gap-2 border-b border-secondary px-6">
                <button
                    onClick={() => setActiveTab('thread')}
                    className={`px-4 py-3 text-sm font-medium transition-colors ${activeTab === 'thread'
                        ? 'border-b-2 border-brand-600 text-brand-600'
                        : 'text-tertiary hover:text-primary'
                        }`}
                >
                    {t('shipper.responses.tabs.thread') || t('shipper.responses.thread') || 'Thread'}
                </button>
                {quote.extractedData && (
                    <button
                        onClick={() => setActiveTab('extracted')}
                        className={`px-4 py-3 text-sm font-medium transition-colors ${activeTab === 'extracted'
                            ? 'border-b-2 border-brand-600 text-brand-600'
                            : 'text-tertiary hover:text-primary'
                            }`}
                    >
                        {t('shipper.responses.tabs.extractedData') || t('detail.tabs.extractedData') || 'Dati Estratti'}
                    </button>
                )}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-6">
                {activeTab === 'thread' && (
                    <div className="space-y-4">
                        {/* Quote originale FF */}
                        <div className="rounded-lg bg-secondary p-4 border border-secondary">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <div className="text-xs font-semibold text-primary">
                                        {quote.fromName || quote.from}
                                    </div>
                                        <Badge color="brand" size="sm">
                                            {t('shipper.responses.originalQuote') || 'Preventivo Originale'}
                                        </Badge>
                                </div>
                                <div className="text-xs text-tertiary">
                                    {quote.receivedAt.toLocaleDateString('it-IT', {
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    })}
                                </div>
                            </div>
                            <div className="text-sm font-medium text-primary mb-2">
                                {quote.subject}
                            </div>
                            <div className="text-sm text-primary whitespace-pre-wrap">
                                {quote.body}
                            </div>
                        </div>

                        {/* Eventuale richiesta modifica shipper */}
                        {quote.modificationRequestBody && (
                            <div className="rounded-lg bg-warning-50/10 p-4 border border-warning-500/20">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <div className="text-xs font-semibold text-primary">
                                            {t('shipper.responses.you') || 'Tu'}
                                        </div>
                                        <Badge color="warning" size="sm">
                                            {t('shipper.responses.modificationRequested') || 'Modifica Richiesta'}
                                        </Badge>
                                    </div>
                                </div>
                                <div className="text-sm text-primary whitespace-pre-wrap">
                                    {quote.modificationRequestBody}
                                </div>
                            </div>
                        )}

                        {/* Eventuale controproposta FF */}
                        {quote.modificationResponseBody && (
                            <div className="rounded-lg bg-secondary p-4 border border-secondary">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <div className="text-xs font-semibold text-primary">
                                            {quote.fromName || quote.from}
                                        </div>
                                        <Badge color="success" size="sm">
                                            {t('shipper.responses.counterProposal') || 'Controproposta'}
                                        </Badge>
                                    </div>
                                </div>
                                <div className="text-sm text-primary whitespace-pre-wrap">
                                    {quote.modificationResponseBody}
                                </div>
                            </div>
                        )}

                        {/* Booking number quando quote è accettata */}
                        {quote.status === 'accepted' && quote.bookingNumber && (
                            <div className="rounded-lg bg-success-50/10 p-4 border border-success-500/20">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <CheckCircle className="h-4 w-4 text-success-600" />
                                        <div className="text-xs font-semibold text-primary">
                                            {t('shipper.responses.bookingNumber') || 'Booking Number'}
                                        </div>
                                        <Badge color="success" size="sm">
                                            {t('shipper.responses.received') || 'Ricevuto'}
                                        </Badge>
                                    </div>
                                </div>
                                <div className="text-lg font-semibold text-success-600 mt-2">
                                    {quote.bookingNumber}
                                </div>
                                <div className="text-xs text-tertiary mt-1">
                                    {t('shipper.responses.bookingNumberShared') || 'Il Freight Forwarder ha condiviso il booking number per questa quote accettata.'}
                                </div>
                            </div>
                        )}

                        {quote.status === 'accepted' && quote.bookingNumberRequested && !quote.bookingNumber && (
                            <div className="rounded-lg bg-warning-50/10 p-4 border border-warning-500/20">
                                <div className="flex items-center gap-2">
                                    <AlertCircle className="h-4 w-4 text-warning-600" />
                                    <div className="text-sm text-primary">
                                        {t('shipper.responses.bookingNumberPending') || 'Il Freight Forwarder sta richiedendo il booking number al vettore. Riceverai una notifica quando sarà disponibile.'}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'extracted' && quote.extractedData && (
                    <div>
                        <div className="rounded-lg bg-secondary p-4">
                            <ExtractedDataPanel data={quote.extractedData} />
                        </div>
                    </div>
                )}
            </div>

            {/* Action Buttons */}
            {quote.status === 'pending' && !isActionDisabled && (
                <div className="flex-shrink-0 px-6 py-4 border-t border-secondary bg-secondary/30">
                    <div className="flex flex-col gap-3">
                        <div className="text-sm font-semibold text-primary mb-1">
                            {t('shipper.responses.actions') || 'Azioni'}
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Button
                                onClick={() => onAccept?.(quote.id)}
                                color="primary"
                                size="md"
                                iconLeading={CheckCircle}
                            >
                                {t('shipper.responses.accept') || 'Accetta'}
                            </Button>
                            <Button
                                onClick={() => onReject?.(quote.id)}
                                color="primary-destructive"
                                size="md"
                                iconLeading={X}
                            >
                                {t('shipper.responses.reject') || 'Rifiuta'}
                            </Button>
                            <Button
                                onClick={() => {
                                    setIsModificationEditorOpen(true);
                                }}
                                color="secondary"
                                size="md"
                                iconLeading={AlertCircle}
                            >
                                {t('shipper.responses.requestModification') || 'Richiedi Modifica'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Inline modification request editor */}
            {quote.status === 'pending' && !isActionDisabled && isModificationEditorOpen && (
                <div className="flex-shrink-0 px-6 py-4 border-t border-secondary bg-secondary">
                    <div className="space-y-4">
                        <h3 className="text-sm font-semibold text-primary">
                            {t('pages:quotes:shipper.responses.modificationEditorTitle', { defaultValue: 'Richiesta modifica preventivo' })}
                        </h3>
                        <Input
                            label={t('pages:quotes:shipper.responses.modificationSubject', { defaultValue: 'Oggetto' })}
                            value={modificationSubject}
                            onChange={setModificationSubject}
                            size="md"
                        />
                        <TextArea
                            label={t('pages:quotes:shipper.responses.modificationBody', { defaultValue: 'Testo richiesta modifica' })}
                            value={modificationBody}
                            onChange={setModificationBody}
                            rows={6}
                            placeholder={t('pages:quotes:shipper.responses.modificationBodyPlaceholder', { defaultValue: 'Descrivi le modifiche richieste (es. prezzo, condizioni, tempi)...' })}
                        />
                        <div className="flex justify-end gap-3">
                            <Button
                                onClick={() => {
                                    setIsModificationEditorOpen(false);
                                    setModificationBody('');
                                }}
                                color="secondary"
                                size="sm"
                            >
                                {t('common:cancel') || 'Annulla'}
                            </Button>
                            <Button
                                onClick={() => {
                                    if (modificationBody.trim()) {
                                        onRequestModification?.(quote.id, modificationBody, modificationSubject);
                                        setIsModificationEditorOpen(false);
                                        setModificationBody('');
                                    }
                                }}
                                color="primary"
                                size="sm"
                                iconLeading={Save01}
                                isDisabled={!modificationBody.trim()}
                            >
                                {t('pages:quotes:shipper.responses.modificationSend', { defaultValue: 'Invia richiesta modifica' })}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

